import { API_BASE_URL } from '$lib/config';
import { apiFetch } from './client';
import { getAccessToken } from './auth';
import { meEmailReviewApi } from './me';
import type {
  AdminApiKey,
  AdminEmailAccount,
  AdminUser,
  BackfillState,
  BackfillStatus,
  CustomFieldDef,
  EmailAccountTestResult,
  EmailReviewGroup,
  LeadRoute,
  LeadSettings,
  LoginEvent,
  Paginated,
  Stats,
  TrackedDomain,
  Vertical
} from '$lib/types';

export const adminApiKeysApi = {
  requests: (page = 1) => apiFetch<{ results: Array<{ id: number; api_key_id: number | null; key_name?: string | null; method: string; path: string; status: number; client_ip: string; created_at: string }>; count: number }>(`/admin/api-keys/requests?limit=25&offset=${(page - 1) * 25}`, { cache: 'no-store' }),
  list: () => apiFetch<AdminApiKey[]>('/admin/api-keys', { cache: 'no-store' }),
  create: (body: { name: string }) =>
    apiFetch<{ key: string; api_key: AdminApiKey }>('/admin/api-keys', {
      method: 'POST', body: JSON.stringify(body), cache: 'no-store'
    }),
  revoke: (id: number) =>
    apiFetch<AdminApiKey>(`/admin/api-keys/${id}/revoke`, { method: 'POST' })
};

function toQuery(params: Record<string, string | number | undefined> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export const adminUsersApi = {
  list: (params?: { q?: string; page?: number; per_page?: number }) =>
    apiFetch<Paginated<AdminUser>>(`/admin/users${toQuery(params)}`),
  create: (body: Record<string, unknown>) =>
    apiFetch<AdminUser>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Record<string, unknown>) =>
    apiFetch<AdminUser>(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<{ detail: string }>(`/admin/users/${id}`, { method: 'DELETE' }),
  resetTotp: (id: number) =>
    apiFetch<{ detail: string }>(`/admin/users/${id}/reset-totp`, { method: 'POST' }),
  resetPassword: (id: number) =>
    apiFetch<{ detail: string; temp_password: string }>(`/admin/users/${id}/reset-password`, {
      method: 'POST'
    })
};

export const adminLoginsApi = {
  forUser: (id: number, params?: { page?: number; per_page?: number }) =>
    apiFetch<Paginated<LoginEvent>>(`/admin/users/${id}/logins${toQuery(params)}`),
  recent: (params?: { limit?: number }) =>
    apiFetch<{ results: LoginEvent[] }>(`/admin/logins${toQuery(params)}`)
};

export const adminVerticalsApi = {
  list: () => apiFetch<{ results: Vertical[]; count: number }>('/admin/verticals'),
  create: (body: Record<string, unknown>) =>
    apiFetch<Vertical>('/admin/verticals', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Record<string, unknown>) =>
    apiFetch<Vertical>(`/admin/verticals/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) =>
    apiFetch<{ detail: string }>(`/admin/verticals/${id}`, { method: 'DELETE' })
};

export const adminCustomFieldsApi = {
  list: (params?: { vertical_id?: number; entity?: string }) =>
    apiFetch<{ results: CustomFieldDef[]; count: number }>(`/admin/custom-fields${toQuery(params)}`),
  create: (body: Record<string, unknown>) =>
    apiFetch<CustomFieldDef>('/admin/custom-fields', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Record<string, unknown>) =>
    apiFetch<CustomFieldDef>(`/admin/custom-fields/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),
  remove: (id: number) =>
    apiFetch<{ detail: string }>(`/admin/custom-fields/${id}`, { method: 'DELETE' })
};

export const adminStatsApi = {
  get: () => apiFetch<Stats>('/admin/stats')
};

export const adminLeadRoutesApi = {
  list: () => apiFetch<{ results: LeadRoute[]; count: number }>('/admin/lead-routes'),
  create: (body: Record<string, unknown>) =>
    apiFetch<LeadRoute>('/admin/lead-routes', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Record<string, unknown>) =>
    apiFetch<LeadRoute>(`/admin/lead-routes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) =>
    apiFetch<{ detail: string }>(`/admin/lead-routes/${id}`, { method: 'DELETE' })
};

export const adminLeadSettingsApi = {
  get: () => apiFetch<LeadSettings>('/admin/lead-settings'),
  update: (body: Record<string, unknown>) =>
    apiFetch<LeadSettings>('/admin/lead-settings', { method: 'PUT', body: JSON.stringify(body) })
};

export const adminTrackedDomainsApi = {
  list: () => apiFetch<{ results: TrackedDomain[]; count: number }>('/admin/tracked-domains'),
  create: (body: Record<string, unknown>) =>
    apiFetch<TrackedDomain>('/admin/tracked-domains', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: number) =>
    apiFetch<{ detail: string }>(`/admin/tracked-domains/${id}`, { method: 'DELETE' })
};

// Admin-only "manage any user's mailbox" surface — same shape/semantics as
// meEmailAccountApi (see $lib/api/me), but keyed by any user, not
// ownership-scoped (internal/api/handlers/admin_email_accounts.go).
export const adminEmailAccountsApi = {
  list: () => apiFetch<{ results: AdminEmailAccount[] }>('/admin/email-accounts'),
  create: (body: Record<string, unknown>) =>
    apiFetch<AdminEmailAccount>('/admin/email-accounts', { method: 'POST', body: JSON.stringify(body) }),
  save: (id: number, body: Record<string, unknown>) =>
    apiFetch<AdminEmailAccount>(`/admin/email-accounts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => apiFetch<{ detail: string }>(`/admin/email-accounts/${id}`, { method: 'DELETE' }),
  test: (id: number, body?: Record<string, unknown>) =>
    apiFetch<EmailAccountTestResult>(`/admin/email-accounts/${id}/test`, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    }),
  syncNow: (id: number) =>
    apiFetch<{ ran: boolean; account?: AdminEmailAccount; detail?: string }>(`/admin/email-accounts/${id}/sync`, {
      method: 'POST'
    }),
  // Scopes the collection above to one user, matching meEmailAccountApi's
  // shape exactly (list/create/save/remove/test/syncNow) — this is what lets
  // EmailAccountConnect.svelte be reused verbatim for "connect/manage
  // mailboxes on behalf of user X" instead of forking its markup. list()
  // filters client-side (the underlying endpoint has no per-user filter —
  // fine at this org's scale, same "no pagination" call as EmailAccessPage);
  // create() injects user_id so the caller never has to remember to.
  forUser: (userId: number) => ({
    list: () =>
      adminEmailAccountsApi
        .list()
        .then((res) => ({ results: res.results.filter((a) => a.user_id === userId) })),
    create: (body: Record<string, unknown>) => adminEmailAccountsApi.create({ ...body, user_id: userId }),
    save: adminEmailAccountsApi.save,
    remove: adminEmailAccountsApi.remove,
    test: adminEmailAccountsApi.test,
    syncNow: adminEmailAccountsApi.syncNow
  })
};

// Admin-only "act on behalf of a user" review-queue/backfill surface —
// internal/api/handlers/email_review.go's adminListEmailReview/
// adminIgnoreEmailSender/adminStartEmailBackfill/adminEmailBackfillStatus.
// forUser(...) returns the same shape as meEmailReviewApi/meEmailBackfillApi
// so EmailReviewQueue.svelte's row actions work unmodified against either.
// markInternal is deliberately NOT re-pointed at a per-user route: marking an
// address internal is an org-wide effect with no per-user variant on the
// backend (any email-access caller, including an admin, uses the same /me
// route regardless of whose queue triggered it) — see RegisterAdminEmailReview's
// comment.
export const adminEmailReviewApi = {
  forUser: (userId: number) => ({
    list: () => apiFetch<{ groups: EmailReviewGroup[] }>(`/admin/users/${userId}/email-review`),
    ignore: (counterparty: string) =>
      apiFetch<{ detail: string }>(`/admin/users/${userId}/email-review/ignore`, {
        method: 'POST',
        body: JSON.stringify({ counterparty })
      }),
    ignoreBatch: (counterparties: string[]) =>
      apiFetch<{ detail: string; count: number }>(`/admin/users/${userId}/email-review/ignore`, {
        method: 'POST',
        body: JSON.stringify({ counterparties })
      }),
    markInternal: meEmailReviewApi.markInternal
  })
};

export const adminEmailBackfillApi = {
  forUser: (userId: number) => ({
    start: (counterparty: string) =>
      apiFetch<{ detail: string; status: BackfillState; position: number }>(`/admin/users/${userId}/email-backfill`, {
        method: 'POST',
        body: JSON.stringify({ counterparty })
      }),
    status: () => apiFetch<BackfillStatus>(`/admin/users/${userId}/email-backfill/status`)
  })
};

// CSV export triggers a real browser download rather than going through
// apiFetch's JSON handling, so it needs its own fetch + auth header.
export async function exportLeadsCSV(params: Record<string, string | number | undefined> = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}/admin/leads/export${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!response.ok) throw new Error(`Export failed: HTTP ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'leads.csv';
  a.click();
  URL.revokeObjectURL(url);
}
